/*
 * The component contract, as a factory.
 *
 * Every component in this kit accepts the same four things on top of its own
 * props -- a forwarded ref, `className`, `style` and `scale` -- and spreads
 * everything else onto its root element. Before this, that contract was
 * hand-written per component and had drifted four ways: BeTextControl
 * destructured `style` and dropped it on the floor, BeMenuField leaked it onto
 * the inner <select>, six components had no `style` prop at all, and NONE of
 * the ~32 forwarded a ref. `rootStyle` was written from scratch nine times.
 *
 * The shape is Workday Canvas Kit's `createComponent`, whose docstring is the
 * argument for a factory over a convention: it "enforces React ref forwarding,
 * `as` prop, display name". You cannot get a component out of `beComponent`
 * without a forwarded ref, so that half cannot be forgotten. The remaining half
 * -- actually spreading `rest` onto the root -- is covered by
 * tests/invariants/base-type.test.tsx, which renders every export and asserts
 * that `id`, `data-*`, `className`, `style` and `ref` all reach the root.
 *
 * `as` IS DELIBERATELY NOT PART OF THIS. Carbon and Canvas Kit put a
 * polymorphic `as` on everything; our control elements are semantically
 * load-bearing in a way theirs are not -- a BeCheckBox is a <label> wrapping a
 * real <input>, and rendering it `as="div"` would produce something that is not
 * a checkbox to the accessibility tree or to the CSS. So `as` stays where it is
 * already correct and already used: the layout primitives and BeText
 * (`<BeStack as="ol">`, `<BeText as="code">`, `<BeRow as="li">`).
 */

import { type ComponentPropsWithoutRef, type CSSProperties, type ElementType, forwardRef, type ReactNode, type Ref } from "react";

/** What every Be component accepts on top of its element's DOM attributes. */
export type BeCommon = {
	/** Override the rendering scale; sets `--be-scale` on the root. */
	scale?: number;
	/** Appended AFTER the component's own classes. */
	className?: string;
	style?: CSSProperties;
};

/**
 * A component's full prop type: its own, plus the common four, plus everything
 * the underlying element accepts. That last part is what makes `id`, `title`,
 * every `data-*`, every `aria-*` and every DOM event handler work without a
 * per-component decision.
 */
export type BeProps<E extends ElementType, Own = unknown> = Own & BeCommon & Omit<ComponentPropsWithoutRef<E>, keyof Own | keyof BeCommon>;

/** What `be(...)` returns: spread it onto the root element, last. */
export type BeRootProps = {
	ref: Ref<never>;
	className: string;
	style?: CSSProperties;
};

type ClassArg = string | false | null | undefined;

/**
 * Merge the component's own classes with the caller's, fold `scale` into the
 * style, and carry the ref.
 *
 * SPREAD THIS LAST, after `{...rest}`. Carbon's ordering: rest first so a
 * caller's stray `className` cannot blow away the component's own classes, then
 * ref/className/style explicitly so the merged values win.
 */
export function beRoot(props: BeCommon, ref: Ref<never>, ...classes: ClassArg[]): BeRootProps {
	const { scale, className, style } = props;
	return {
		ref,
		className: [...classes, className].filter(Boolean).join(" "),
		style: scale !== undefined ? ({ ...style, "--be-scale": scale } as CSSProperties) : style,
	};
}

/**
 * Build a component. The render function receives the props and a `be()` helper
 * already bound to this render's ref -- call it with the component's own class
 * names and spread the result onto the root.
 *
 *   export const BeThing = beComponent<BeThingProps, HTMLDivElement>("BeThing", (props, be) => {
 *     const { thing, scale, className, style, ...rest } = props;
 *     return <div {...rest} {...be("be-thing", thing && "is-thing")} />;
 *   });
 *
 * Destructure `scale`, `className` and `style` out of `rest` even though `be()`
 * reads them from `props` -- otherwise they are spread onto the element twice,
 * and the raw `scale` number would land as an invalid DOM attribute.
 *
 * WRAPPER CONTROLS SPLIT THE BUNDLE. A checkbox, radio, text control, spinner
 * and menu field are a <label> around a real form element, and the two halves
 * want different things: `className`/`style` belong on the label (it is what
 * paints), while `ref` and the remaining DOM attributes belong on the CONTROL
 * (you want to focus it, read `.checked`, set `name`). Destructure the ref out
 * and place it yourself:
 *
 *   const { ref, ...root } = be("be-checkbox");
 *   return <label {...root}><input ref={ref} {...rest} /> ... </label>;
 */
export function beComponent<P extends BeCommon, E = HTMLElement>(
	displayName: string,
	render: (props: P, be: (...classes: ClassArg[]) => BeRootProps) => ReactNode,
) {
	// forwardRef hands the render `PropsWithoutRef<P>`, which TS cannot prove is P
	// for an unresolved generic. P never contains `ref` (forwardRef supplies it),
	// so the two are the same type here.
	const Component = forwardRef<E, P>((props, ref) => {
		const p = props as P;
		return render(p, (...classes) => beRoot(p, ref as Ref<never>, ...classes));
	});
	Component.displayName = displayName;
	return Component;
}
